import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMessage } from "@/portal/api";
import { useAuth } from "@/portal/AuthContext";
import { Card, StatusBadge, ServiceTags, STATUS_ORDER, STATUS_META, formatDate } from "@/pages/portal/ui";
import { Button } from "@/components/ui/button";
import { PlusCircle, FileText, ArrowRight, Link2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([
          api.get("/api/portal/stats"),
          api.get("/api/portal/applications"),
        ]);
        setStats(s.data);
        setRecent(a.data.applications.slice(0, 8));
      } catch (e) {
        toast.error(errMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const referralLink = user.referral_code ? `${window.location.origin}/apply/${user.referral_code}` : null;
  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success("Application link copied");
    setTimeout(() => setCopied(false), 1800);
  };

  const isCustomer = user.role === "customer";

  return (
    <div className="mx-auto max-w-6xl space-y-6" data-testid="dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold text-navy">Welcome back, {user.name?.split(" ")[0]}</h2>
          <p className="text-sm text-slatey">
            {isCustomer ? "Track the status of your energy applications." : user.role === "manager" ? "Here's an overview of the full team pipeline." : "Here's an overview of your pipeline."}
          </p>
        </div>
        <Link to="/portal/new">
          <Button className="bg-electric text-white hover:bg-electric-hover" data-testid="dashboard-new-btn">
            <PlusCircle className="mr-1.5 h-4 w-4" /> {isCustomer ? "New Application" : "New Customer"}
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Card className="p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slatey">Total</p>
          <p className="mt-1 font-heading text-2xl font-semibold text-navy" data-testid="stat-total">{stats?.total ?? "—"}</p>
        </Card>
        {STATUS_ORDER.map((s) => (
          <Card key={s} className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-slatey">{STATUS_META[s].label}</p>
            <p className="mt-1 font-heading text-2xl font-semibold text-navy" data-testid={`stat-${s}`}>
              {stats?.by_status?.[s] ?? 0}
            </p>
          </Card>
        ))}
      </div>

      {/* Referral link */}
      {referralLink && (
        <Card className="flex flex-wrap items-center justify-between gap-3 border-energy/30 bg-energy/5 p-4">
          <div className="flex items-center gap-3">
            <Link2 className="h-5 w-5 text-energy" />
            <div>
              <p className="text-sm font-semibold text-navy">Your customer application link</p>
              <p className="break-all text-xs text-slatey" data-testid="referral-link">{referralLink}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={copyLink} data-testid="copy-referral">
            {copied ? <Check className="mr-1.5 h-4 w-4 text-energy" /> : <Copy className="mr-1.5 h-4 w-4" />}
            {copied ? "Copied" : "Copy link"}
          </Button>
        </Card>
      )}

      {/* Recent */}
      <Card>
        <div className="flex items-center justify-between border-b border-line px-5 py-3">
          <h3 className="font-heading text-sm font-semibold text-navy">Recent applications</h3>
          {!isCustomer && (
            <Link to="/portal/applications" className="inline-flex items-center gap-1 text-xs font-medium text-electric hover:underline">
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          )}
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-slatey">Loading…</div>
        ) : recent.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-10 text-center">
            <FileText className="h-8 w-8 text-line" />
            <p className="text-sm text-slatey">No applications yet.</p>
            <Link to="/portal/new" className="text-sm font-medium text-electric hover:underline">Submit your first one →</Link>
          </div>
        ) : (
          <ul className="divide-y divide-line" data-testid="recent-list">
            {recent.map((a) => (
              <li key={a.id}>
                <Link to={`/portal/applications/${a.id}`} className="flex items-center justify-between gap-4 px-5 py-3 transition-colors hover:bg-ice">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-navy">{a.company_name}</p>
                    <p className="text-xs text-slatey">{a.state_name} · {formatDate(a.created_at)}</p>
                  </div>
                  <div className="hidden sm:block"><ServiceTags services={a.services} /></div>
                  <StatusBadge status={a.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
